import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';
import { Usuario } from '../types/auth';

interface AuthContextType {
  usuario: Usuario | null;
  authUser: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        if (session?.user) {
          setAuthUser(session.user);
          try {
            const userData = await authService.getCurrentUser(session.user.id);
            setUsuario(userData);
          } catch (error) {
            console.error('Error loading user data:', error);
            setUsuario(null);
          }
        } else {
          setAuthUser(null);
          setUsuario(null);
        }
      })();
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkSession = async () => {
    try {
      setLoading(true);

      const sessionData = await authService.checkSession();

      if (sessionData) {
        setAuthUser(sessionData.authUser);
        setUsuario(sessionData.usuario);
      } else {
        setAuthUser(null);
        setUsuario(null);
      }
    } catch (error) {
      console.error('Error checking session:', error);
      setAuthUser(null);
      setUsuario(null);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    const { authUser: user, usuario: userData } = await authService.login(email, password);
    setAuthUser(user);
    setUsuario(userData);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setAuthUser(null);
    setUsuario(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (authUser) {
      try {
        const userData = await authService.getCurrentUser(authUser.id);
        setUsuario(userData);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  }, [authUser]);

  const value = useMemo(() => ({
    usuario,
    authUser,
    loading,
    isAuthenticated: !!authUser && !!usuario,
    login,
    logout,
    signOut: logout,
    refreshUser,
  }), [usuario, authUser, loading, login, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('useAuth must be used within an AuthProvider');
    return {
      usuario: null,
      authUser: null,
      loading: false,
      isAuthenticated: false,
      login: async () => {},
      logout: async () => {},
      signOut: async () => {},
      refreshUser: async () => {},
    } as AuthContextType;
  }
  return context;
};
